import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MAIN_APP_BASE_URL } from '../config/env';
import { loginWithCpf } from '../services/authService';
import { setAuthSession } from '../services/authStorage';
import '../styles/LoginPage.css';

const logo3Bet = '/icones/icone-sem-fundo-com-trangulo.png';

const formatCpf = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const redirectTarget = params.get('redirect') || '/clubs';

  const [cpf, setCpf] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  useEffect(() => {
    const rememberedCpf = localStorage.getItem('rememberCpf');
    if (rememberedCpf) {
      setCpf(formatCpf(rememberedCpf));
      setRememberMe(true);
    }
  }, []);

  const formatLoginError = (err: any): string => {
    const retryAfter = Number(err?.retryAfter);

    if (err?.status === 429 && Number.isFinite(retryAfter) && retryAfter > 0) {
      return err?.message || `Muitas tentativas de login. Tente novamente em ${retryAfter}s.`;
    }

    return err?.message || 'Nao foi possivel entrar no portal financeiro.';
  };

  const handleLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const cleanCpf = cpf.replace(/\D/g, '');
      if (cleanCpf.length !== 11) {
        throw new Error('Informe um CPF valido.');
      }
      if (!password.trim()) {
        throw new Error('Informe a senha para entrar no portal financeiro.');
      }

      const result = await loginWithCpf(cleanCpf, password);

      setAuthSession({
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        userId: result.user.id,
        userEmail: result.user.email || '',
        userName: result.user.nome || '',
        userAvatar:
          result.user.avatar?.profileUrl || result.user.avatar?.seatUrl || result.user.avatarUrl || '',
        userBalance: result.user.saldo || 0,
      });

      if (rememberMe) {
        localStorage.setItem('rememberCpf', cleanCpf);
      } else {
        localStorage.removeItem('rememberCpf');
      }

      navigate(redirectTarget.startsWith('/') ? redirectTarget : '/clubs', { replace: true });
    } catch (err: any) {
      setError(formatLoginError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="wallet-login-page">
      <div className="wallet-login-shell">
        <div className="wallet-login-shell__noise" />
        <div className="wallet-login-shell__halo wallet-login-shell__halo--top" />
        <div className="wallet-login-shell__halo wallet-login-shell__halo--bottom" />

        <header className="wallet-login-brand">
          <img src={logo3Bet} alt="3BET Wallet" className="wallet-login-brand__logo" />
        </header>

        <section className="wallet-login-card">
          {error && (
            <div className="wallet-login-error" role="alert" aria-live="polite">
              {error}
            </div>
          )}

          <form onSubmit={handleLogin} className="wallet-login-form">
            <label className="wallet-login-field">
              <span>CPF</span>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="username"
                placeholder="000.000.000-00"
                value={cpf}
                onChange={(event) => setCpf(formatCpf(event.target.value))}
                disabled={loading}
              />
            </label>

            <label className="wallet-login-field">
              <span>Senha</span>
              <div className="wallet-login-password">
                <input
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="Sua senha"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  disabled={loading}
                />
                <button type="button" onClick={() => setShowPassword((prev) => !prev)} disabled={loading}>
                  {showPassword ? 'Ocultar' : 'Mostrar'}
                </button>
              </div>
            </label>

            <label className="wallet-login-remember">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(event) => setRememberMe(event.target.checked)}
                disabled={loading}
              />
              <span>Lembrar CPF neste navegador</span>
            </label>

            <button type="submit" className="wallet-login-primary" disabled={loading}>
              {loading ? 'Conectando...' : 'Entrar no portal'}
            </button>

            <button
              type="button"
              className="wallet-login-secondary"
              onClick={() => navigate('/login/recovery')}
              disabled={loading}
            >
              Recuperar senha
            </button>
          </form>

          <div className="wallet-login-actions">
            <a href={MAIN_APP_BASE_URL}>Voltar para o app principal</a>
          </div>
        </section>
      </div>
    </div>
  );
};

export default LoginPage;
